import { useState } from 'react'
import { toast } from 'react-toastify'
import { startCase } from 'lodash'
import { AuthService } from '../../../services/auth.service'
import { AxiosService } from '../../../services/axios.service'
import Button from '../../Button/Button'
import OTPVerification from '../OTPVerification'
import style from '../phone-verification.module.scss'
import type { AxiosError } from 'axios'
import { ApiErrorResponse } from '../../../types/api.types'
import PhoneInput from '../../PhoneInput/PhoneInput'

const authService = AuthService.getInstance()

export interface SignupProps {
  onSuccess?: () => void
  onSwitchToLogin?: () => void
}

enum SignupStep {
  INFO_INPUT = 'INFO_INPUT',
  OTP_VERIFICATION = 'OTP_VERIFICATION',
}

export const Signup = ({ onSuccess, onSwitchToLogin }: SignupProps) => {
  const [currentStep, setCurrentStep] = useState<SignupStep>(SignupStep.INFO_INPUT)
  const [isLoading, setIsLoading] = useState(false)

  // Form fields
  const [phoneNumber, setPhoneNumber] = useState<string>('')
  const [email, setEmail] = useState<string>('')
  const [fullName, setFullName] = useState<string>('')

  // Validation errors
  const [errors, setErrors] = useState<{
    phoneNumber?: string
    email?: string
    fullName?: string
  }>({})

  const [fullPhoneNumber, setFullPhoneNumber] = useState<string>('')

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {}

    if (!phoneNumber) {
      newErrors.phoneNumber = 'Phone number is required'
    }

    if (!email) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Please enter a valid email address'
    }

    if (!fullName) {
      newErrors.fullName = 'Full name is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const sendOTP = async (e?: React.FormEvent) => {
    e?.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      setIsLoading(true)

      // Remove all spaces and formatting characters except +
      const cleanPhone = fullPhoneNumber.replace(/[\s\-()]/g, '')

      // Extract country code with + sign and phone number
      if (cleanPhone.startsWith('+')) {
        const phoneStr = cleanPhone.substring(1) // Remove + sign temporarily
        const countryCode = `+${phoneStr.substring(0, phoneStr.length - 10)}` // Add + back to country code
        const phoneNumber = phoneStr.substring(phoneStr.length - 10) // Last 10 digits

        await authService.sendOTP(countryCode, phoneNumber, email, 'email')
        setCurrentStep(SignupStep.OTP_VERIFICATION)
        toast.success('OTP sent successfully!')
      } else {
        toast.error('Invalid phone number format')
      }
    } catch (_error) {
      const error = _error as AxiosError<ApiErrorResponse>
      toast.error(error.response?.data?.message || 'Failed to send OTP')
    } finally {
      setIsLoading(false)
    }
  }

  const verifyOTP = async (otp: string) => {
    try {
      setIsLoading(true)

      // Extract phone number for OTP verification (last 10 digits, no spaces)
      const cleanPhone = fullPhoneNumber.replace(/[\s\-()]/g, '')

      if (cleanPhone.startsWith('+')) {
        const phoneStr = cleanPhone.substring(1) // Remove + sign
        const countryCode = `+${phoneStr.substring(0, phoneStr.length - 10)}` // Country code with +
        const phoneNumber = phoneStr.substring(phoneStr.length - 10) // Last 10 digits

        const capitalizedName = startCase(fullName)
        setFullName(capitalizedName)

        const res = await authService.verifyOTP({
          countryCode,
          phoneNumber,
          email,
          name: capitalizedName,
          otp,
          via: 'email',
        })

        if (res.status === 200) {
          const axiosService = AxiosService.getInstance()
          axiosService.setAccessToken(res.data.accessToken)
          axiosService.setRefreshToken(res.data.refreshToken)
          await authService.getUserDataAndUpdateStore()
          reset()
          onSuccess?.()
        } else {
          toast.error('Invalid OTP')
        }
      } else {
        toast.error('Invalid phone number format')
      }
    } catch (_error) {
      const error = _error as AxiosError<ApiErrorResponse>
      toast.error(error.response?.data?.message || 'Invalid OTP')
    } finally {
      setIsLoading(false)
    }
  }

  const handleResendOTP = async () => {
    if (email && phoneNumber) {
      await sendOTP()
    }
  }

  const reset = () => {
    setCurrentStep(SignupStep.INFO_INPUT)
    setPhoneNumber('')
    setEmail('')
    setFullName('')
    setErrors({})
  }

  return (
    <>
      {currentStep === SignupStep.INFO_INPUT && (
        <form className={style.form} onSubmit={sendOTP}>
          <p className={style.description}>Enter your details to create an account</p>

          <div className={style.phoneInputContainer}>
            <PhoneInput
              name="phoneNumber"
              placeholder="Phone Number"
              errorMessage={errors.phoneNumber}
              value={phoneNumber}
              onChange={(value, fullPhoneNumber) => {
                setPhoneNumber(value)
                setFullPhoneNumber(fullPhoneNumber)
                setErrors(prev => ({ ...prev, phoneNumber: undefined }))
              }}
            />
          </div>

          <div className={style.emailInputContainer}>
            <input
              type="email"
              className={style.emailInput}
              placeholder="Email Address"
              value={email}
              onChange={e => {
                setEmail(e.target.value)
                setErrors(prev => ({ ...prev, email: undefined }))
              }}
            />
            {errors.email && <span className={style.errorMessage}>{errors.email}</span>}
          </div>

          <div className={style.fullNameInputContainer}>
            <input
              type="text"
              className={style.fullNameInput}
              placeholder="Full Name"
              value={fullName}
              onChange={e => {
                setFullName(e.target.value)
                setErrors(prev => ({ ...prev, fullName: undefined }))
              }}
              onBlur={e => {
                const capitalizedName = startCase(e.target.value)
                setFullName(capitalizedName)
              }}
            />
            {errors.fullName && <span className={style.errorMessage}>{errors.fullName}</span>}
          </div>

          <div className={style.buttonGroup}>
            <Button
              label={isLoading ? 'Sending...' : 'Send OTP'}
              type="submit"
              fullWidth={true}
              disabled={isLoading}
            />
            {onSwitchToLogin && (
              <button
                type="button"
                className={style.linkButton}
                onClick={onSwitchToLogin}
                disabled={isLoading}
              >
                Already have an account? Login
              </button>
            )}
          </div>
        </form>
      )}

      {currentStep === SignupStep.OTP_VERIFICATION && (
        <OTPVerification
          email={email}
          isLoading={isLoading}
          onVerify={verifyOTP}
          onResend={handleResendOTP}
          onBack={() => setCurrentStep(SignupStep.INFO_INPUT)}
          backButtonText="Change Phone Number or Email"
        />
      )}
    </>
  )
}

export default Signup
